import React, { useEffect, useRef, useState } from "react";
import { useHistory } from 'react-router-dom';
import { encode } from "Helpers/enc";

//Styling and Components
import "./search-bar.scss";
import {SearchOutlined } from  '@ant-design/icons';
import { fetchProductList } from "Services/inventory.services";
import { SkeletonLoader } from "@life-line-lab/components-react";
import { Drawer } from 'antd';
import {isArray, map} from 'lodash';
import { useTranslation } from "react-i18next";
import {Helmet} from "react-helmet";

function SearchBar() {
  const history = useHistory();
  const { t } = useTranslation();
  const [fetchedData, setFetchedData] = useState<any>([]);
  const [wordEntered, setWordEntered] = useState("");
  const [isLoading, setIsLoading] = useState(false); 
  const [visible, setVisible] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  /**
   * Also focus the input when show the drawer.
   *
   * Added a setTimeout for the focus() method to work
   * @see https://github.com/software-mansion/react-native-screens/issues/472#issuecomment-843122746
   */
  const showDrawer = (e) => {
    e?.preventDefault();

    setVisible(true);

    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
  };

  const onClose = () => {
    setVisible(false);
  };

  const handleFilter = async (word: string) => {
    word = word.trim();

    const filters = {
      name: word,
      alias: word,
      brand: word,
      active: true
    };

    const pagination = {
      offset: 0,
      limit: 20,
    };

    const sorting = {
      sortBy: 'name',
      mode: 'ASC',
    }

    try {
      const itemsData = await fetchProductList(filters, pagination, sorting);

      if (isArray(itemsData) && !itemsData.error) {
        setFetchedData(itemsData);
      }
    } catch (error) {
      setFetchedData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (wordEntered.trim().length < 3) {
      setIsLoading(false);
      setFetchedData([]);
      return;
    }

    setIsLoading(true);

    const timeoutFunc = setTimeout(() => {
      handleFilter(wordEntered);
    }, 300);  // delay a little bit for user to type, only fetch from BE when user done typing

    return () => clearTimeout(timeoutFunc);
  }, [wordEntered]);

  const handleClickProduct = (inventory) => {
    history.push('/products/' + encode(inventory.inventoryID));
    onClose();
  }

  return (
    <div>
      <Helmet>
        {map(fetchedData, prod => {
          return <meta name="description" content={prod.product.productName}/>
        })}
      </Helmet>
      <div className="searchbar-wrapper" onClick={showDrawer}>
        <div className="search-icon-group" onClick={showDrawer}>
          <SearchOutlined className="searchIcon"/>
          <div className="search-icon-group--label">{t("txt.searchProducts")}</div>
        </div>
      </div>
      <Drawer
        className="searchDrawer"
        placement='top'
        width='100%'
        height='350px'
        onClose={onClose}
        visible={visible}
        mask={true}
        maskClosable
        closable
      >
        <div className="search">
          <input
            type="text"
            aria-label="search"
            placeholder={t("txt.searchProducts")}
            value={wordEntered}
            ref={searchInputRef}
            onChange={e => setWordEntered(e.target.value)}
          />
          {
            isLoading &&
            <div className="dataResult with-skeleton">
              <SkeletonLoader type="default"/>
            </div>
          }
          {
            !isLoading && fetchedData.length > 0 &&
            <div className="dataResult">
              {fetchedData.map((value, key) => {
                return (
                  <div
                    key={key}
                    className="dataItem"
                    onClick={() => handleClickProduct(value)}
                  >
                    <img
                    src={value.featuredImage?.image || value.productImages?.[0]?.image  || '/assets/images/product-placeholder.png'}
                    width={40}
                    onError={({ currentTarget }) => {
                      currentTarget.onerror = null; // prevents looping
                      currentTarget.src = '/assets/images/product-placeholder.png';
                    }}
                    />
                    <p>{value.product.productName} </p>
                  </div>
                );
              })}
            </div>
          }
          {
            !isLoading && fetchedData.length === 0 && wordEntered.trim().length >= 3 &&
            <div className="dataResult">
              <div className="text-center" style={{ marginTop: '25px' }}>
                <img src={process.env.ACCOUNT_TYPE == 'P2U' ? "/assets/images/product/no-result-p2u.png" : "/assets/images/product/no-result-drezy.png"} width="150" />
                <div><h3>{t('label.NoResultsFound')}</h3></div>
              </div>
            </div>
          }
        </div>
      </Drawer>
    </div>
  );
}

export default SearchBar;
